import {
  createContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useContext,
} from "react";
import {
  getDateString,
  today,
  getDateInLocalTZFromDateStr,
  getWeekStart,
  getWeekEnd,
} from "../../../utils/datetime";

import { getWeekOfShifts } from "../../../services/api/timekeeping";
import { useMessageDebounced } from "../../../hooks/useMessageDebounced";

/**
 * Context that is SPECIFIC to the Timekeeping app. Anything generic should be in higher
 * level context like CoreContext.
 *
 * @context TimekeepingContext
 *
 * @typedef {Object} TimekeepingContextType
 * @property {string} date - The currently selected date.
 * @property {Function} setDate - Function to update the selected date.
 * @property {boolean} showClockOutContent - Whether to show clock out content.
 * @property {Function} setShowClockOutContent - Function to update clock out content visibility.
 * @property {boolean} isLoading - Whether data is currently being loaded.
 * @property {Function} setIsLoading - Function to update loading state.
 * @property {string} weekStart - The start date of the current week.
 * @property {String} weekEnd - The end date of the current week (calculated from weekStart).
 * @property {Function} setWeekStart - Function to update the start date of the week.
 * @property {Object} weeklyShiftData - Data for the weekly shifts.
 * @property {Object} dailyShiftData - Data for the daily shifts.
 * @property {Function} setDailyShiftData - Function to update daily shift data.
 * @property {Function} fetchWeeklyShifts - Function to fetch weekly shifts.
 * @property {string} message - A message to display.
 * @property {Function} setMessageDebounced - Function to set the message with debounce.
 * @property {Function} clearMessage - Function to clear the message.
 */
export const TimekeepingContext = createContext(null);

/**
 * Read TimekeepingContext. Throws if used outside the weekly timekeeping provider.
 *
 * @returns {Object}
 */
/**
 * Reads TimekeepingContext. Throws if used outside a Timekeeping provider.
 *
 * @returns {Object} Timekeeping context value.
 */
export function useTimekeepingContext() {
  const context = useContext(TimekeepingContext);
  if (!context) {
    throw new Error(
      "useTimekeepingContext must be used within a TimekeepingProvider"
    );
  }
  return context;
}

/**
 * Build weekly timekeeping state for clock-in, schedule, hours-log, and worker timesheet.
 *
 * @param {string|null} [initialDateParam=null] YYYY-MM-DD to open on.
 * @param {number|string|null} [userIdParam=null] Optional worker PK for supervisor views.
 * @returns {Object} Value passed to `TimekeepingContext.Provider`.
 */
/**
 * Builds weekly/daily shift state for clock-in, schedule, and worker timesheet.
 *
 * @param {string|null} [initialDateParam=null] Date key from the URL, or today.
 * @param {number|null} [userIdParam=null] Optional worker id when viewing another user.
 * @returns {Object} Timekeeping context value.
 */
export function useTimekeepingCtx(initialDateParam = null, userIdParam = null) {
  // State variables
  const [date, setDate] = useState(initialDateParam || getDateString(today()));
  const [userId] = useState(userIdParam);
  const [weekStart, setWeekStart] = useState(null);
  const weekEnd = getDateString(getWeekEnd(getDateInLocalTZFromDateStr(date))); // 6 for Saturday end
  const [isLoading, setIsLoading] = useState(true);
  // Data states
  const [weeklyShiftData, setWeeklyShiftData] = useState(null);
  const [completedShifts, setCompletedShifts] = useState([]);
  const [dailyShiftData, setDailyShiftData] = useState(null);
  // Animation states
  const [showClockOutContent, setShowClockOutContent] = useState(false);

  // Use the message debounced hook to manage messages across components
  const { message, setMessageDebounced, clearMessage } = useMessageDebounced();
  // initialDateParam is now handled in the useState above
  // Get start date for current week
  useEffect(() => {
    const baseDate = getDateInLocalTZFromDateStr(date);
    setWeekStart(getDateString(getWeekStart(baseDate, 0))); // 0 for Sunday start (will fix globally to be sunday soon)
  }, [date]);

  // Shifts per day and completed shifts per week are extracted from weeklyShiftData
  useEffect(() => {
    const shifts =
      weeklyShiftData?.days.find((day) => day.date === date)?.shifts || [];
    setDailyShiftData(shifts);
  }, [date, weeklyShiftData]);
  useEffect(() => {
    if (weeklyShiftData) {
      const completed = weeklyShiftData.days
        .map((day, dayIndex) => ({
          ...day,
          dayIndex, // Add original day index for editing
          shifts: day.shifts
            .map((shift, shiftIndex) => ({
              ...shift,
              shiftIndex, // Add original shift index for editing
            }))
            .filter((shift) => shift.stage === "Complete Shift"),
        }))
        .filter((day) => day.shifts.length > 0);
      setCompletedShifts(completed);
    }
  }, [weeklyShiftData]);

  // Create a fetchWeeklyShifts function that can be called from any component
  const fetchWeeklyShifts = useCallback(
    async (startDate = weekStart, targetUserId = userId) => {
      try {
        setIsLoading(true);
        const data = await getWeekOfShifts(startDate, targetUserId);
        setWeeklyShiftData(data);
        setIsLoading(false);
        return data;
      } catch (error) {
        console.error("Error fetching weekly shifts:", error);
        setIsLoading(false);
        return null;
      }
    },
    [weekStart, userId]
  );

  // Fetch weekly shift data when weekStart or userId changes
  useEffect(() => {
    if (weekStart) {
      fetchWeeklyShifts(weekStart, userId);
    }
  }, [fetchWeeklyShifts, weekStart, userId]);

  const contextValue = useMemo(
    // Memo so if another component calls it doesn't re-run context unless changed
    () => ({
      date,
      setDate,
      showClockOutContent,
      setShowClockOutContent,
      isLoading,
      setIsLoading,
      weekStart,
      weekEnd,
      setWeekStart,
      weeklyShiftData,
      completedShifts,
      setCompletedShifts,
      dailyShiftData,
      setDailyShiftData,
      fetchWeeklyShifts,
      // Add message state and functions to the context
      message,
      setMessageDebounced,
      clearMessage,
    }),
    [
      date,
      showClockOutContent,
      isLoading,
      weekStart,
      weekEnd,
      weeklyShiftData,
      dailyShiftData,
      fetchWeeklyShifts,
      completedShifts,
      setCompletedShifts,
      // Include message-related dependencies in the dependency array
      message,
      setMessageDebounced,
      clearMessage,
    ]
  );

  return {
    ...contextValue,
  };
}

export default useTimekeepingCtx; // Export the hook as default for backward compatibility
